import { useState } from 'react';
import { Tabs } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';

import { Text } from '../../../Text';
import ChatHeader from '../ChatHeader/ChatHeader';
import {
  PostEmptyState,
  PostEmptyStateCommish,
} from '../EmptyStates/PostEmptyState/PostEmptyState';
import ChatDisabled from '../EmptyStates/ChatDisabled/ChatDisabled';
import ChatUnavailableOnWeb from '../EmptyStates/ChatUnavailableOnWeb/ChatUnavailableOnWeb';
import NoChats from '../EmptyStates/NoChats/NoChats';
import NoChatCommissioner from '../EmptyStates/NoChatCommissioner/NoChatCommissioner';
import ChatFooter from '../ChatFooter/ChatFooter';
import HomeIndicator from '../HomeIndicator/HomeIndicator';
import ChatMessagesList from '../ChatMessagesList/ChatMessagesList';
import { PostsList } from '../PostsList/PostsList';
import {
  ChatBubbleSkeleton,
  generateChatSkeletonPattern,
} from '../ChatBubbleSkeleton/ChatBubbleSkeleton';
import { PostSkeleton } from '../PostSkeleton/PostSkeleton';
import { useVisualViewportHeight } from '~/hooks/useVisualViewportHeight';

import type { ChatDrawerProps } from './types';
import useStyles from './ChatDrawer.styles';

function ChatDrawer({
  title,
  commissionerHandle,
  isCommissioner = false,
  onToggleChatEnabled,
  isChatGloballyEnabled = true,
  isChatEnabled = false,
  isPostsEnabled = true,
  isChatWebEnabled = false,
  isPostsWebEnabled = false,
  hasUnreadMessages = false,
  onClose,
  onTabChange,
  activeTab = 'messages',
  onChatMessageSubmit,
  onOpenPostModal,
  onAddMedia,
  isSubmitting = false,
  messages = [],
  posts = [],
  currentUserHandle,
  onReportClick,
  onDeleteClick,
  onReactionSelect,
  onReactionRemove,
  availableReactions = [],
  isLoadingReactions = false,
  getUserReactions,
  // Infinite scroll props
  loaderRef,
  scrollContainerRef,
  hasNextPage,
  isFetchingNextPage,
  // Flags to override empty state when Pusher indicates messages exist (separate per tab)
  hasReceivedChatPusherMessage = false,
  hasReceivedPostPusherMessage = false,
  isSidePanel = false,
  // Loading states
  isLoadingChats = false,
  isLoadingMessages = false,
  isLoadingPosts = false,
  // Draft message persistence
  draftMessage: externalDraftMessage,
  onDraftMessageChange,
}: ChatDrawerProps) {
  const { t } = useTranslation('chat');
  const { classes, cx } = useStyles();
  const [currentTab, setCurrentTab] = useState(activeTab);

  // Set up visual viewport height tracking for mobile keyboard handling
  useVisualViewportHeight();
  // Use external draft message if provided, otherwise use internal state
  const [internalDraftMessage, setInternalDraftMessage] = useState('');
  const draftMessage =
    externalDraftMessage !== undefined ? externalDraftMessage : internalDraftMessage;
  const setDraftMessage = onDraftMessageChange || setInternalDraftMessage;

  // Use localized title as default
  const displayTitle = title || t('title');

  const handleTabChange = (tab: 'messages' | 'posts') => {
    setCurrentTab(tab);
    onTabChange?.(tab);
  };

  // Extracted conditions for footer visibility and message composer
  const shouldShowPostsFooter =
    isCommissioner && currentTab === 'posts' && isPostsWebEnabled && isPostsEnabled;
  const shouldShowMessagesFooter = currentTab === 'messages' && isChatEnabled && isChatWebEnabled;
  const shouldShowAnyFooter = shouldShowPostsFooter || shouldShowMessagesFooter;

  // Don't show mobile app message on posts tab (regardless of commissioner status)
  const shouldShowMobileAppMessage =
    !shouldShowAnyFooter && !isChatWebEnabled && currentTab !== 'posts';

  return (
    <div
      className={isSidePanel ? cx(classes.container, classes.modalContainer) : classes.container}
    >
      <ChatHeader
        title={displayTitle}
        isCommissioner={isCommissioner}
        hasUnreadMessages={hasUnreadMessages}
        onClose={onClose}
        isSidePanel={isSidePanel}
        onHeaderClick={onClose}
        onToggleChatEnabled={onToggleChatEnabled}
      />

      <Tabs
        value={currentTab}
        onTabChange={(value) => handleTabChange(value as 'messages' | 'posts')}
        classNames={{
          root: classes.tabsContainer,
          tabsList: classes.tabsList,
          tab: classes.tab,
        }}
      >
        <Tabs.List>
          <Tabs.Tab value="messages">
            <Text
              variant="label-small"
              className={cx(classes.tabText, {
                [classes.activeTabText]: currentTab === 'messages',
              })}
            >
              {t('tabs.messages')}
            </Text>
          </Tabs.Tab>
          <Tabs.Tab value="posts">
            <Text
              variant="label-small"
              className={cx(classes.tabText, { [classes.activeTabText]: currentTab === 'posts' })}
            >
              {t('tabs.posts')}
            </Text>
          </Tabs.Tab>
        </Tabs.List>
      </Tabs>

      <div className={classes.content}>{renderContent()}</div>

      {/* Chat Footer - show for different conditions */}
      {/* For commissioners on Posts tab - show "Send a post" button */}
      {shouldShowPostsFooter && (
        <ChatFooter
          variant="post-button"
          onOpenPostModal={() => {
            onOpenPostModal?.();
          }}
          disabled={isSubmitting}
        />
      )}

      {/* For messages tab when chat is enabled and feature flag allows it */}
      {shouldShowMessagesFooter && (
        <ChatFooter
          variant="input"
          placeholder={t('placeholders.chat')}
          onSubmit={onChatMessageSubmit}
          onAddMedia={onAddMedia}
          disabled={isSubmitting}
          autoFocus={isSidePanel} // Only auto-focus on desktop (side panel), not mobile drawer
          value={draftMessage}
          onChange={setDraftMessage}
        />
      )}

      {/* Message Composer - only show when no footer is present and not on posts tab */}
      {shouldShowMobileAppMessage && (
        <div className={classes.messageComposer}>
          <div className={classes.composerContent}>
            <Text variant="body-large" className={classes.composerText}>
              {t('messageComposer.mobileAppMessage')}
            </Text>
          </div>
        </div>
      )}

      {/* Home Indicator - only visible on mobile drawer (not side panel) */}
      {!isSidePanel && <HomeIndicator />}
    </div>
  );

  // Helper function to render content based on current state
  function renderContent() {
    // Show skeleton while loading initial chat data
    if (isLoadingChats) {
      const chatSkeletonPattern = generateChatSkeletonPattern();

      return (
        <div className={classes.skeletonContainer}>
          {currentTab === 'posts' ? (
            <>
              <PostSkeleton />
              <PostSkeleton />
            </>
          ) : (
            <>
              {chatSkeletonPattern.map((pattern) => (
                <ChatBubbleSkeleton
                  key={pattern.id}
                  variant={pattern.variant}
                  showMultipleLines={pattern.showMultipleLines}
                />
              ))}
            </>
          )}
        </div>
      );
    }

    // Check global chat availability first (applies to both tabs)
    if (!isChatGloballyEnabled) {
      return (
        <div className={classes.emptyState}>
          <ChatUnavailableOnWeb />
        </div>
      );
    }

    // Check tab-specific disabled state (commissioner disabled chat/posts independently)
    const isCurrentTabDisabled =
      (currentTab === 'messages' && !isChatEnabled) || (currentTab === 'posts' && !isPostsEnabled);

    if (isCurrentTabDisabled) {
      return (
        <div className={classes.emptyState}>
          <ChatDisabled />
        </div>
      );
    }

    // Show messages/posts if we have them, are loading, or if Pusher indicates messages exist for THIS tab
    const currentTabData = currentTab === 'posts' ? posts : messages;
    const isCurrentTabLoading = currentTab === 'posts' ? isLoadingPosts : isLoadingMessages;
    const hasReceivedPusherForCurrentTab =
      currentTab === 'posts' ? hasReceivedPostPusherMessage : hasReceivedChatPusherMessage;

    if (currentTabData.length > 0 || hasReceivedPusherForCurrentTab || isCurrentTabLoading) {
      if (currentTab === 'posts') {
        return (
          <PostsList
            posts={posts}
            currentUserHandle={currentUserHandle}
            onReportClick={onReportClick}
            onReactionSelect={onReactionSelect}
            onReactionRemove={onReactionRemove}
            availableReactions={availableReactions}
            isLoadingReactions={isLoadingReactions}
            getUserReactions={getUserReactions}
            loaderRef={loaderRef}
            scrollContainerRef={scrollContainerRef}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            isLoading={isLoadingPosts}
          />
        );
      }

      return (
        <ChatMessagesList
          messages={messages}
          currentUserHandle={currentUserHandle}
          onReportClick={onReportClick}
          onDeleteClick={onDeleteClick}
          onReactionSelect={onReactionSelect}
          onReactionRemove={onReactionRemove}
          availableReactions={availableReactions}
          isLoadingReactions={isLoadingReactions}
          getUserReactions={getUserReactions}
          loaderRef={loaderRef}
          scrollContainerRef={scrollContainerRef}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          isLoading={isLoadingMessages}
        />
      );
    }

    // Show appropriate empty states based on current tab
    if (currentTab === 'posts') {
      if (isCommissioner) {
        return (
          <div className={classes.emptyState}>
            <PostEmptyStateCommish commissionerHandle={commissionerHandle} />
          </div>
        );
      }
      return (
        <div className={classes.emptyState}>
          <PostEmptyState commissionerHandle={commissionerHandle} />
        </div>
      );
    }

    // Messages tab empty state
    return (
      <div className={classes.emptyState}>
        {isCommissioner ? <NoChatCommissioner /> : <NoChats />}
      </div>
    );
  }
}

export default ChatDrawer;
